import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import { useFormikContext, FormikErrors } from 'formik'
import { useSelector, useDispatch } from 'react-redux'
import qs from 'query-string'
import {
    setTemplateFilter,
    selectTemplatesFilter,
    getCampaignTemplates,
    OBJECTIVE,
    selectTemplateData,
    CampaignTemplateStep,
} from 'state/campaignTemplates'
import Text from 'particles/Text'
import { useNewTranslation } from 'hooks'
import styled from 'particles/themes/styled'
import { FormikCheckboxGroup } from 'particles/Checkbox/Group'
import { FormikRadioButtonGroup } from 'particles/RadioButton/Group'
import RectangleButton from 'particles/Button/RectangleButton'
import {
    PLATFORM_CONTENT_TYPE_ENUM,
    PLATFORM_CONTENT_TYPE_FILTER,
    PlatformContentTypeFilter,
} from 'services/api/constants'
import { TemplateFilterSchema } from './index'
import { TemplateContentContainer } from '../style'
import { fieldIdentifiers } from 'components/Shared/Comment'
import EditTemplateRestricted from 'components/Shared/Permission/EditTemplateRestricted'
import {
    WriteReviewOptions,
    getCredibilityOptions,
    getPostPlatformOptions,
    getRequireCreateContentOptions,
    getRequirePurchaseOptions,
} from './options'

interface CampaignRequirementProps {
    completeToNextSection: () => void
    backPreviousSection: () => void
    validateSpecificField: (fieldNames: (keyof TemplateFilterSchema)[]) => Promise<FormikErrors<TemplateFilterSchema>>
    isReadOnly?: boolean
}

const CampaignRequirement: React.FC<CampaignRequirementProps> = ({
    completeToNextSection,
    backPreviousSection,
    validateSpecificField,
    isReadOnly = false,
}) => {
    const [t] = useNewTranslation()
    const params = qs.parse(location.search)
    const { templateData: templateDataUuid } = params
    const dispatch = useDispatch()
    const templateFilter = useSelector(selectTemplatesFilter)
    const { values, setTouched } = useFormikContext<TemplateFilterSchema>()
    const requirePurchaseOptions = getRequirePurchaseOptions(t)
    const templateData = useSelector(selectTemplateData)
    const objective = templateData?.objective ?? templateFilter.objective
    const postPlatformOptions = getPostPlatformOptions(t, objective)
    const requireCreateContentOptions = getRequireCreateContentOptions(t)
    const [requiredConditionalInputType, setRequiredConditionalInputType] = useState<PLATFORM_CONTENT_TYPE_ENUM | null>(
        null,
    )

    useEffect(() => {
        const platformContentType = values.platform_content_types
        if (platformContentType?.includes(PLATFORM_CONTENT_TYPE_ENUM.INSTAGRAM_STORY)) {
            setRequiredConditionalInputType(PLATFORM_CONTENT_TYPE_ENUM.INSTAGRAM_STORY)
        } else if (platformContentType?.includes(PLATFORM_CONTENT_TYPE_ENUM.WRITE_REVIEW)) {
            setRequiredConditionalInputType(PLATFORM_CONTENT_TYPE_ENUM.WRITE_REVIEW)
        } else {
            setRequiredConditionalInputType(null)
        }
    }, [values])

    const onNextClick = () => {
        if (templateDataUuid) {
            // if campaign already be created, no needs to call filter since all filter parameters is read only
            completeToNextSection()
        } else {
            setTouched({
                ig_story_create_content_required: true,
                get_product_required: true,
                platform_content_types: true,
                review_priority: true,
                review_type: true,
            })
            validateSpecificField([
                'get_product_required',
                'platform_content_types',
                'ig_story_create_content_required',
                'review_priority',
                'review_type',
            ]).then((errors: FormikErrors<TemplateFilterSchema>) => {
                const noError = Object.keys(errors).length === 0
                if (noError) {
                    let platform_content_types_filter: PlatformContentTypeFilter[] = []
                    if (values.platform_content_types) {
                        platform_content_types_filter = [...values.platform_content_types]
                    }
                    const igStoryIndex = platform_content_types_filter.indexOf(
                        PLATFORM_CONTENT_TYPE_ENUM.INSTAGRAM_STORY,
                    )

                    if (igStoryIndex !== -1) {
                        if (values.ig_story_create_content_required) {
                            platform_content_types_filter[igStoryIndex] =
                                PLATFORM_CONTENT_TYPE_FILTER.INSTAGRAM_STORY_UGC
                        } else {
                            platform_content_types_filter[igStoryIndex] =
                                PLATFORM_CONTENT_TYPE_FILTER.INSTAGRAM_STORY_NON_UGC
                        }
                    }

                    const writeReviewIndex = platform_content_types_filter.indexOf(
                        PLATFORM_CONTENT_TYPE_ENUM.WRITE_REVIEW,
                    )

                    if (writeReviewIndex !== -1) {
                        if (
                            values.review_type === 'review_text_only' &&
                            values.review_priority === 'review_with_approval'
                        ) {
                            platform_content_types_filter[writeReviewIndex] =
                                PLATFORM_CONTENT_TYPE_FILTER.WRITE_REVIEW_NON_IMAGE_APPROVAL
                        } else if (
                            values.review_type === 'review_text_only' &&
                            values.review_priority === 'review_no_approval'
                        ) {
                            platform_content_types_filter[writeReviewIndex] =
                                PLATFORM_CONTENT_TYPE_FILTER.WRITE_REVIEW_NON_IMAGE_NON_APPROVAL
                        } else if (
                            values.review_type === 'review_text_image' &&
                            values.review_priority === 'review_with_approval'
                        ) {
                            platform_content_types_filter[writeReviewIndex] =
                                PLATFORM_CONTENT_TYPE_FILTER.WRITE_REVIEW_IMAGE_APPROVAL
                        } else if (
                            values.review_type === 'review_text_image' &&
                            values.review_priority === 'review_no_approval'
                        ) {
                            platform_content_types_filter[writeReviewIndex] =
                                PLATFORM_CONTENT_TYPE_FILTER.WRITE_REVIEW_IMAGE_NON_APPROVAL
                        }
                    }

                    const filterProps = _.omit(templateFilter, ['brandUuid'])
                    dispatch(
                        getCampaignTemplates({
                            filter: {
                                ...filterProps,
                                get_product_required: values.get_product_required,
                                platform_content_types:
                                    platform_content_types_filter && platform_content_types_filter.join(','),
                            },
                            type: 'filter',
                        }),
                    )
                    dispatch(
                        setTemplateFilter({
                            get_product_required: values.get_product_required,
                            platform_content_types: platform_content_types_filter,
                        }),
                    )
                    completeToNextSection()
                }
            })
        }
    }
    return (
        <CampaignRequirementContainer data-testid="test-campaign-requirement-template-page">
            <div className="form-title">
                <Text scale="H4" color="primary">
                    {t('campaign.template.step1.campaignRequirements.title')}
                </Text>
            </div>

            <FormikRadioButtonGroup
                name="get_product_required"
                title={t('campaign.template.purchaseNeeds.title')}
                options={requirePurchaseOptions}
                isRequired
                tooltip={t('campaign.template.purchaseNeeds.tooltips')}
                isReadOnly={isReadOnly}
                labelSize="l"
                identifier={`${CampaignTemplateStep.CAMPAIGN_REQUIREMENTS}_${fieldIdentifiers.templatePurchaseNeeds}`}
            />

            {objective !== OBJECTIVE.credibility ? (
                <FormikCheckboxGroup
                    name="platform_content_types"
                    title={t('campaign.template.platformPost.title')}
                    options={postPlatformOptions}
                    isRequired
                    tooltip={t('campaign.template.platformPost.tooltips')}
                    isReadOnly={isReadOnly}
                    labelSize="l"
                    identifier={`${CampaignTemplateStep.CAMPAIGN_REQUIREMENTS}_${fieldIdentifiers.templatePlatformPost}`}
                />
            ) : (
                <FormikCheckboxGroup
                    name="platform_content_types"
                    title={t('campaign.template.credibilityNeed.title')}
                    options={getCredibilityOptions(t)}
                    isRequired
                    tooltip={t('campaign.template.credibilityArea.tooltips')}
                    isReadOnly={isReadOnly}
                    labelSize="l"
                    identifier={`${CampaignTemplateStep.CAMPAIGN_REQUIREMENTS}_${fieldIdentifiers.templateCredibilityNeeds}`}
                />
            )}

            {requiredConditionalInputType !== null && (
                <div className="conditional-input">
                    {requiredConditionalInputType === PLATFORM_CONTENT_TYPE_ENUM.INSTAGRAM_STORY && (
                        <FormikRadioButtonGroup
                            name="ig_story_create_content_required"
                            title={t('campaign.template.platformPost.instagramStoryUCG.title')}
                            options={requireCreateContentOptions}
                            isRequired
                            tooltip={t('campaign.template.platformPost.instagramStoryUCG.tooltips')}
                            isReadOnly={isReadOnly}
                            labelSize="l"
                            dataTestId="test-instagramStoryUCG"
                            identifier={`${CampaignTemplateStep.CAMPAIGN_REQUIREMENTS}_${fieldIdentifiers.templateRequireCreateContent}`}
                        />
                    )}

                    {requiredConditionalInputType === PLATFORM_CONTENT_TYPE_ENUM.WRITE_REVIEW && (
                        <WriteReviewOptions isReadOnly={isReadOnly} />
                    )}
                </div>
            )}

            <EditTemplateRestricted>
                <div className="button-group">
                    <div className="button">
                        <RectangleButton
                            type={'button'}
                            form={'outlined'}
                            size={'l'}
                            label={t('button.back')}
                            onClick={backPreviousSection}
                        />
                    </div>
                    <div className="button">
                        <RectangleButton type={'button'} size={'l'} label={t('button.next')} onClick={onNextClick} />
                    </div>
                </div>
            </EditTemplateRestricted>
        </CampaignRequirementContainer>
    )
}

export const CampaignRequirementContainer = styled(TemplateContentContainer)`
    max-width: 770px;
    .conditional-input {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[400]};
        width: 600px;
        border-radius: 12px;
        padding 12px;
        background: ${({ theme }): string => theme.colors.surface.infoPastel};
        border: 1px solid ${({ theme }): string => theme.colors.border.primary};
    }
    .button-group {
        display: flex;
        gap: ${({ theme }): string => theme.spacing[600]};
    }
`

export default CampaignRequirement
